import { default as React, useCallback, useState } from 'react';
import {
  Color,
  Column,
  Row,
  SelectorOptionType,
  splitBorderWidthProps,
  Text,
  TextLink,
  useTelemetry,
} from '@snowflake/core-ui';
import { SignupFormDataState, useSignupFormData } from './SignupFormDataContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { CardTemplate } from '../../shared/CardTemplate';
import { countries, nonPriorityCountries, priorityCountries } from '@signup/shared';
import { SignupCardButton } from './SignupCardButton';
import { SignupFormInputs } from './SignupFormInputs';
import { useSignupFormController } from './SignupFormControllerContext';
import { EmailAgreement } from '../../shared/EmailAgreement';
import { useWaitlistInputConfigs } from './inputConfigs';
import { InlinedSelector } from '../../shared/InlinedSelector';
import { SignupEventName } from '../../../../utils/SignupLogger';

interface CortexCodeWaitListCardProps {
  continueNext: () => void;
}

const FormOneFieldNames: (keyof Pick<
  SignupFormDataState,
  'firstName' | 'lastName' | 'email' | 'countryIsoCode'
>)[] = ['firstName', 'lastName', 'email', 'countryIsoCode'];

export const CortexCodeWaitListCard = (props: CortexCodeWaitListCardProps) => {
  const formData = useSignupFormData();
  const { setField, countryIsoCode, optInEmailAgreement, optOutEmailAgreement } = formData;
  const { formatMessage } = useIntl();
  const { continueNext } = props;
  const { validateField, submitWaitlistForm } = useSignupFormController();
  const [forceRequired, setForceRequired] = useState(false);
  const inputConfigs = useWaitlistInputConfigs();
  const { logAction } = useTelemetry();
  const [formOneStartTime] = useState<number>(Date.now() / 1000);

  const countryOptions: SelectorOptionType<string>[] = [
    ...priorityCountries.map(country => {
      return {
        value: country.isoCode,
        label: country.label,
      };
    }),
    { divider: true },
    ...nonPriorityCountries.map(country => {
      return {
        value: country.isoCode,
        label: country.label,
      };
    }),
  ];

  const onGetStartedClicked = useCallback(async () => {
    const invalidFieldNames = FormOneFieldNames.filter(
      fieldName => validateField(fieldName, formData[fieldName]) !== true,
    );

    if (invalidFieldNames.length > 0) {
      logAction(
        SignupEventName.CORTEX_CODE_WAITLIST_FORM_VALIDATION_ERROR_ON_SUBMIT,
        'ui_click',
        'CortexCodeWaitListCard',
        {
          firstFormValidationErrors: invalidFieldNames,
          flowType: 'cortex_code_waitlist',
        },
      );

      setForceRequired(true);
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 0));

    logAction(
      SignupEventName.CORTEX_CODE_WAITLIST_FORM_COMPLETE,
      'ui_click',
      'CortexCodeWaitListCard',
      {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        country: countries.find(c => c.isoCode === countryIsoCode)?.value || '',
        formId: formOneStartTime.toString(),
        formOneCompletionTime: Date.now() / 1000 - formOneStartTime,
        flowType: 'cortex_code_waitlist',
      },
    );

    continueNext();

    await submitWaitlistForm();
  }, [
    continueNext,
    validateField,
    formData,
    logAction,
    submitWaitlistForm,
    formOneStartTime,
    countryIsoCode,
  ]);

  return (
    <CardTemplate>
      <Column width="100%" rowGap="16px">
        <Row>
          <Column width="100%">
            <Text fontSize="20px" fontWeight="700">
              Join the waitlist
            </Text>
            <Text fontSize="14px" lineHeight="24px" color={Color.Gray70}>
              Be first in line when we launch
            </Text>
          </Column>
        </Row>
        <Row>
          <SignupFormInputs configs={inputConfigs} forceRequired={forceRequired} />
        </Row>

        <Row>
          <EmailAgreement
            optInEmailAgreement={optInEmailAgreement}
            setOptInEmailAgreement={val => setField('optInEmailAgreement', val)}
            optOutEmailAgreement={optOutEmailAgreement}
            setOptOutEmailAgreement={val => setField('optOutEmailAgreement', val)}
            countryIsoCode={countryIsoCode}
          />
        </Row>

        <Row>
          <SignupCardButton width="100%" onClick={onGetStartedClicked}>
            <FormattedMessage id="Sign up" />
          </SignupCardButton>
        </Row>

        <Row align="center" width="100%">
          <Text fontSize="12px" color={Color.Gray60}>
            By clicking Sign up, you agree to our{' '}
            <TextLink
              href="https://www.snowflake.com/privacy-policy/"
              target="_blank"
              fontSize="12px"
            >
              Privacy Notice
            </TextLink>
          </Text>
        </Row>
        <Row align="center" fontSize="12px" color={Color.Gray70} columnGap="8px">
          <Column verticalAlign="middle">
            <Text>
              <FormattedMessage id="Country:" />
            </Text>
          </Column>
          <Column>
            <InlinedSelector
              {...splitBorderWidthProps(0)}
              options={countryOptions}
              value={countryIsoCode}
              label={countries.find(c => c.isoCode === countryIsoCode)?.label}
              placeholder={formatMessage({ id: 'Select a country' })}
              selectValue={item => {
                setField('countryIsoCode', item);
              }}
              buttonTextColor={forceRequired && !countryIsoCode ? Color.Red50 : undefined}
            />
          </Column>
        </Row>
      </Column>
    </CardTemplate>
  );
};
